// ============================================================================
// BomPrintSheet — Stückliste als Angebots-/Bestell-Druckblatt (A4)
// ----------------------------------------------------------------------------
// pvXpert-Moat „1-Klick-Bestellung" (lokaler Export, KEIN Webshop-Call):
// rendert die on-the-fly erzeugte API-BOM (BillOfMaterials) als druckbares
// A4-Blatt. Ergänzt den CSV-Export um ein menschenlesbares Deliverable.
//
// Reines Frontend (Druck), kein Persist. Sicherheitsinvarianten sichtbar:
// ENTWURF · kein Angebot · keine Freigabe · menschliche Prüfung erforderlich.
// Preise sind Netto-Richtwerte (Phase 0), keine Verbindlichkeit.
// ============================================================================
import { eur, num } from "@/lib/format.js";
import type { BillOfMaterials } from "@/modules/planning/bom-export.js";
import { bomDisplayLines } from "@/modules/planning/bom-export.js";

export interface BomPrintSheetProps {
  bom: BillOfMaterials;
  projectName?: string;
  customerName?: string;
  onClose: () => void;
}

const PRINT_CSS = `
  @media print {
    body * { visibility: hidden; }
    #bomx-print-overlay, #bomx-print-overlay * { visibility: visible; }
    #bomx-print-overlay {
      position: absolute !important;
      left: 0; top: 0; width: 100%;
      background: white !important;
      padding: 0; margin: 0; overflow: visible !important;
    }
    .bomx-no-print { display: none !important; visibility: hidden !important; }
  }
  @page { size: A4 portrait; margin: 15mm; }
`;

export function BomPrintSheet({ bom, projectName, customerName, onClose }: BomPrintSheetProps) {
  const rows = bomDisplayLines(bom);
  const today = new Date().toLocaleDateString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <>
      <style>{PRINT_CSS}</style>
      <div
        id="bomx-print-overlay"
        style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", overflowY: "auto" }}
        data-testid="bom-print-overlay"
      >
        {/* Steuerleiste — wird beim Drucken ausgeblendet */}
        <div
          className="bomx-no-print"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "8px 16px", background: "rgba(0,0,0,0.35)", position: "sticky", top: 0, zIndex: 10 }}
        >
          <button
            onClick={() => window.print()}
            style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: "#1e3a5f", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            Drucken (A4)
          </button>
          <button
            onClick={onClose}
            data-testid="bom-print-close"
            style={{ padding: "8px 14px", fontSize: 13, background: "#fff", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" }}
          >
            Schliessen
          </button>
        </div>

        {/* A4-Blatt */}
        <div
          style={{
            background: "white",
            maxWidth: 794,
            margin: "12px auto 40px",
            padding: "16mm 14mm",
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: 11,
            color: "#1a1a1a",
            boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
          }}
          data-testid="bom-print-sheet"
        >
          {/* Kopf */}
          <div style={{ borderBottom: "3px solid #1e3a5f", paddingBottom: 12, marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1e3a5f", letterSpacing: -0.5 }}>Stückliste / Materialaufstellung</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Unverbindliche Materialaufstellung &mdash; ENTWURF &mdash; Kein Angebot
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 10, color: "#6b7280" }}>
              <div style={{ fontWeight: 700, color: "#1e3a5f", fontSize: 13 }}>ELMETIC</div>
              <div>Elektrotechnik &amp; Installation</div>
              <div>Tirol &middot; Kärnten</div>
            </div>
          </div>

          {/* Meta */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14, fontSize: 10 }}>
            <div>
              {(customerName || projectName) && (
                <table style={{ borderCollapse: "collapse" }}>
                  <tbody>
                    {customerName && (
                      <tr>
                        <td style={{ color: "#6b7280", paddingRight: 8, paddingBottom: 2 }}>Kunde:</td>
                        <td style={{ fontWeight: 600 }}>{customerName}</td>
                      </tr>
                    )}
                    {projectName && (
                      <tr>
                        <td style={{ color: "#6b7280", paddingRight: 8 }}>Projekt:</td>
                        <td>{projectName}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div>Erstellt: <strong>{today}</strong></div>
              <div>Anlagengrösse: <strong>{num(bom.systemSizeKwp, 2)} kWp</strong></div>
              <div style={{ color: "#b91c1c", marginTop: 2 }}>Richtwert &mdash; keine Gültigkeitsdauer</div>
            </div>
          </div>

          {/* Stücklisten-Tabelle */}
          <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 12, borderBottom: "1px solid #d1d5db", paddingBottom: 3 }}>
            Material / Komponenten (Richtwerte, netto exkl. USt)
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 14 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #d1d5db", color: "#6b7280" }}>
                <th style={{ textAlign: "left", padding: "3px 0", width: 32, fontWeight: 400 }}>Pos</th>
                <th style={{ textAlign: "left", padding: "3px 0", fontWeight: 400 }}>Artikel</th>
                <th style={{ textAlign: "right", padding: "3px 0", width: 70, fontWeight: 400 }}>Menge</th>
                <th style={{ textAlign: "left", padding: "3px 6px", width: 50, fontWeight: 400 }}>Einheit</th>
                <th style={{ textAlign: "right", padding: "3px 0", width: 100, fontWeight: 400 }}>Gesamt netto</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((line) => (
                <tr key={line.position} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "3px 0", color: "#6b7280" }}>{line.position}</td>
                  <td style={{ padding: "3px 0" }}>
                    <span style={{ color: "#6b7280" }}>{line.categoryLabel}</span> &middot; {line.description}
                  </td>
                  <td style={{ padding: "3px 0", textAlign: "right" }}>{num(line.quantity, 0)} {line.unit}</td>
                  <td style={{ padding: "3px 6px" }}>{line.unit}</td>
                  <td style={{ padding: "3px 0", textAlign: "right" }}>
                    {line.totalEurNet != null ? eur(line.totalEurNet, 2) : "—"}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #1e3a5f", fontWeight: 700, fontSize: 13 }}>
                <td colSpan={4} style={{ padding: "5px 0", textAlign: "right" }}>MATERIAL GESAMT netto</td>
                <td style={{ textAlign: "right", padding: "5px 0" }} data-testid="bom-print-total-net">{eur(bom.totalEurNet, 2)}</td>
              </tr>
            </tbody>
          </table>

          {/* Annahmen */}
          {bom.assumptions.length > 0 && (
            <div style={{ background: "#f0f4f8", borderRadius: 4, padding: "8px 12px", fontSize: 9.5, color: "#374151", marginBottom: 14, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 3 }}>Annahmen</div>
              {bom.assumptions.map((a, i) => (
                <div key={i}>&bull; {a}</div>
              ))}
            </div>
          )}

          {/* Haftungshinweis */}
          <div
            style={{ marginTop: 8, border: "2px solid #b91c1c", borderRadius: 6, padding: "10px 14px", background: "#fef2f2", fontSize: 9.5, color: "#7f1d1d", lineHeight: 1.6 }}
            data-testid="bom-print-disclaimer"
          >
            <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11 }}>WICHTIGER HINWEIS &mdash; KEIN ANGEBOT</div>
            Diese Stückliste ist eine unverbindliche Materialaufstellung (Richtwert), kein Angebot und keine
            Bestellung. Mengen sind aus der Modulbelegung und einer Standard-Auslegung abgeleitet (siehe
            Annahmen). Technische und kaufmännische Prüfung erforderlich. Verbindliche Komponentenwahl,
            Preise und Netzzulassung werden vor Angebotserstellung durch eine Fachkraft geprüft. Keine
            Freigabe durch KI.
          </div>

          {/* Fusszeile */}
          <div style={{ marginTop: 18, borderTop: "1px solid #d1d5db", paddingTop: 8, fontSize: 9, color: "#9ca3af", display: "flex", justifyContent: "space-between" }}>
            <div>ELMETIC Elektrotechnik &amp; Installation &middot; Tirol &amp; Kärnten</div>
            <div>EnergyOS Planungssystem &middot; Interne Stückliste (canApprove: false)</div>
          </div>
        </div>
      </div>
    </>
  );
}
